import axios from 'axios';
import { isDate } from 'date-fns';

import {
    checkValidityBeforeSave,
    lenientParse,
    validateFields,
    isNumberCellEmpty,
    createDateAsUTC,
} from '../../services/items';

export const getFinancialAndPhysicalInstrument = async (itemIds) => {
    const params = new URLSearchParams();
    itemIds.forEach((itemId) => {
        params.append('itemTypeId', itemId);
    });

    const response = await axios.get(`/items/by-types`, { params });
    return response.data;
};

export const copyHedge = async (hedgeId) => {
    const response = await axios.post(`/items/${hedgeId}/copy`);
    return response.data;
};

export const findHedge = (items, id) => {
    return items.filter((i) => i.id === Number(id))[0];
};

export const addItemToPortfolios = (itemId, payload) => axios.post(`/items/${itemId}/portfolios`, payload);

export const getHedge = async (hedgeId) => {
    const response = await axios.get(`/hedges/${hedgeId}`);
    return response.data;
};

export const deleteHedgeItem = (hedgeId) => axios.delete(`/items/${hedgeId}`);

export const getHedgeFilters = async (hedgeId) => {
    const response = await axios.get(`/hedges/${hedgeId}/filters`);
    return response.data;
};

export const getHedgeInstruments = async (hedgeId, payload) => {
    const data = typeof payload === 'number' ? { instrumentTypeId: payload } : payload;

    const response = await axios.post(`/hedges/${hedgeId}/instruments`, data);

    return response.data;
};

export const modifyHedgeProperty = async (hedgeId, config) => {
    const propertyNamesMap = {
        primaryCommodities: 'PrimaryCommodity',
        counterparties: 'CounterParty',
        secondaryCommodities: 'SecondaryCommodity',
        quantityTypes: 'QuantityType',
        masterAgreements: 'MasterAgreement',
        dates: 'Date',
    };

    const propertyValue =
        config.propertyValue === -1 || config.propertyValue === null
            ? null
            : isDate(config.propertyValue)
            ? createDateAsUTC(config.propertyValue)
            : config.propertyValue.toString();

    const payload = {
        instrumentTypeId: config.instrumentType,
        propertyName: propertyNamesMap[config.propertyName],
        propertyValue,
    };

    const response = await axios.put(`/hedges/${hedgeId}/modify-property`, payload);

    return response.data;
};

export const createInstruments = async (payload) => {
    const response = await axios.post('/hedges/create-instruments', payload);
    return response.data;
};

export const getAdjustmentsForInstrument = async (instrumentId, payload) => {
    const response = await axios.get(`hedges/${instrumentId}/adjustments`, { params: payload });
    return {
        totalCount: response.data.length,
        data: response.data,
    };
};

export const getValidRows = (rows) => {
    return rows
        .map((row) => {
            const { startDate, endDate, expiryDate, transactionDate, ...rest } = row.data;

            const isValid =
                startDate &&
                checkValidityBeforeSave(startDate) &&
                endDate &&
                checkValidityBeforeSave(endDate) &&
                // expiryDate & transactionDate are not required and can be empty, but should be a valid date otherwise
                checkValidityBeforeSave(expiryDate) &&
                checkValidityBeforeSave(transactionDate);

            if (isValid) {
                return {
                    ...rest,
                    startDate: createDateAsUTC(lenientParse(startDate)),
                    endDate: createDateAsUTC(lenientParse(endDate)),
                    expiryDate: expiryDate ? createDateAsUTC(lenientParse(expiryDate)) : null,
                    transactionDate: transactionDate ? createDateAsUTC(lenientParse(transactionDate)) : null,
                };
            }

            return null;
        })
        .filter((row) => row !== null);
};

export const getValidAdjustmentRows = (rows) => {
    return rows.map((row) => {
        const { startDate, endDate, ...rest } = row.data;

        let dates = {};
        if (!startDate && !endDate) {
            dates = { startDate, endDate };
        } else if (startDate && !endDate) {
            const isValid = checkValidityBeforeSave(startDate);

            if (isValid) {
                dates = {
                    startDate: createDateAsUTC(lenientParse(startDate)),
                    endDate: null,
                };
            }
        } else if (!startDate && endDate) {
            const isValid = checkValidityBeforeSave(endDate);

            if (isValid) {
                dates = {
                    startDate: null,
                    endDate: createDateAsUTC(lenientParse(endDate)),
                };
            }
        } else if (startDate && endDate) {
            const isValidStartDate = checkValidityBeforeSave(startDate);
            const isValidEndDate = checkValidityBeforeSave(endDate);

            if (isValidStartDate && isValidEndDate) {
                dates = {
                    startDate: createDateAsUTC(lenientParse(startDate)),
                    endDate: createDateAsUTC(lenientParse(endDate)),
                };
            }
        }

        return {
            ...dates,
            ...rest,
        };
    });
};

// Some properties are added dynamically that's why this is passed as props from EditAdjustmentsModal
export const columnSchema = {
    startDate: { name: 'startDate', type: 'date' },
    endDate: { name: 'endDate', type: 'date' },
    scaler: { name: 'scaler', type: 'number' },
    adder: { name: 'adder', type: 'number' },
    min: { name: 'min', type: 'number' },
    max: { name: 'max', type: 'number' },
    dateIntervalName: { name: 'dateIntervalName', type: 'string' },
    linkedObjectName: { name: 'linkedObjectName', type: 'string' },
};

export const validateParsedNewRows = (parsedData, schema) => {
    return parsedData.map((row) => {
        return validateFields([
            {
                ...schema['startDate'],
                value: row.startDate,
            },
            {
                ...schema['endDate'],
                value: row.endDate,
            },
            {
                ...schema['scaler'],
                value: row.scaler,
            },
            {
                ...schema['adder'],
                value: row.adder,
            },
            {
                ...schema['min'],
                value: row.min,
            },
            {
                ...schema['max'],
                value: row.max,
            },
            {
                ...schema['dateIntervalName'],
                value: row.dateIntervalName,
            },
            {
                ...schema['linkedObjectName'],
                value: row.linkedObjectName,
            },
        ]);
    });
};

export const formatParsedNewRows = (parsedData, selectTypes) => {
    return parsedData.map((row) => {
        const { startDate, endDate, dateIntervalName, linkedObjectName, ...rest } = row;

        const dateInterval = selectTypes.dateIntervals.find((type) => type.description === dateIntervalName);
        const linkedObject = selectTypes.linkedObjects.find((type) => type.description === linkedObjectName);

        return {
            startDate: startDate ? createDateAsUTC(lenientParse(startDate)) : null,
            endDate: endDate ? createDateAsUTC(lenientParse(endDate)) : null,
            scaler: isNumberCellEmpty(rest.scaler),
            adder: isNumberCellEmpty(rest.adder),
            min: isNumberCellEmpty(rest.min),
            max: isNumberCellEmpty(rest.max),
            dateIntervalId: dateInterval ? dateInterval.id : null,
            linkedObjectId: linkedObject ? linkedObject.id : null,
        };
    });
};

export const createAdjustments = (payload) => axios.post('/hedges/create-adjustments', payload);

export const updateAdjustmentCells = (payload) => axios.put('/hedges/modify-adjustments', payload);

export const deleteAdjustments = (instrumentId, payload) =>
    axios.delete(`/hedges/${instrumentId}/adjustments`, { data: payload });

export const deleteAllAdjustments = (instrumentId) => axios.delete(`/hedges/${instrumentId}/delete-all-adjustments`);

export const deleteInstruments = (hedgeId, instrumentIds) =>
    axios.delete(`/hedges/${hedgeId}/instruments`, { data: instrumentIds });

export const updateInstruments = (payload) => axios.put(`/hedges/modify-instruments`, payload);

export const gridColumns = [
    { field: 'useFlag', hide: false },
    { field: 'instrumentTypeId', hide: false },
    { field: 'primaryCommodityId', hide: false },
    { field: 'startDate', hide: false },
    { field: 'endDate', hide: false },
    { field: 'peakPeriod', hide: false },
    { field: 'price1', hide: false },
    { field: 'price2', hide: false },
    { field: 'price3', hide: false },
    { field: 'price4', hide: false },
    { field: 'price5', hide: false },
    { field: 'price6', hide: false },
    { field: 'quantity', hide: false },
    { field: 'quantityTypeId', hide: false },
    { field: 'counterPartyId', hide: false },
    { field: 'transactionDate', hide: false },
    { field: 'expiryDate', hide: false },
    { field: 'masterAgreementId', hide: false },
    { field: 'secondaryCommodityId', hide: false },

    // expanded
    { field: 'sourceLocation', hide: false },
    { field: 'systemIdentifier', hide: false },
    { field: 'expansion1', hide: false },
    { field: 'expansion2', hide: false },
    { field: 'expansion3', hide: false },
    { field: 'expansion4', hide: false },
    { field: 'expansion5', hide: false },
    { field: 'expansion6', hide: false },
    { field: 'expansion7', hide: false },
    { field: 'expansion8', hide: false },
    { field: 'expansion9', hide: false },
    { field: 'expansion10', hide: false },
    { field: 'expansion11', hide: false },
    { field: 'expansion12', hide: false },
    { field: 'expansion13', hide: false },
    { field: 'expansion14', hide: false },
    { field: 'expansion15', hide: false },
    { field: 'expansion16', hide: false },
    { field: 'expansion17', hide: false },
    { field: 'expansion18', hide: false },
    { field: 'expansion19', hide: false },
    { field: 'expansion20', hide: false },

    // these are not shown to the user
    { field: 'instrumentId', hide: true },
    { field: 'itemId', hide: true },
    { field: 'peakPeriodId', hide: true },
];

export const deleteAllInstruments = (hedgeId) => axios.delete(`/hedges/${hedgeId}/delete-all-instruments`);

export const validateParsedNewRowsOnMainGrid = (parsedData, schema) => {
    return parsedData
        .map((row) => {
            const fields = Object.values(schema).map((v) => ({ ...v, value: row[v.name] }));

            return validateFields(fields);
        })
        .filter((row) => row !== null);
};

export const columnSchemaOnMainGrid = {
    useFlag: { name: 'useFlag', type: 'bool-number', isRequired: true },
    instrumentTypeId: { name: 'instrumentTypeId', type: 'string', isRequired: true },
    primaryCommodityId: { name: 'primaryCommodityId', type: 'string', isRequired: true },
    startDate: { name: 'startDate', type: 'date', isRequired: true },
    endDate: { name: 'endDate', type: 'date', isRequired: true },
    peakPeriodId: { name: 'peakPeriodId', type: 'string', isRequired: true },
    price1: { name: 'price1', type: 'number' },
    price2: { name: 'price2', type: 'number' },
    price3: { name: 'price3', type: 'number' },
    price4: { name: 'price4', type: 'number' },
    price5: { name: 'price5', type: 'number' },
    price6: { name: 'price6', type: 'number' },
    quantity: { name: 'quantity', type: 'number' },
    quantityTypeId: { name: 'quantityTypeId', type: 'string', isRequired: true },
    counterPartyId: { name: 'counterPartyId', type: 'string' },
    transactionDate: { name: 'transactionDate', type: 'date' },
    expiryDate: { name: 'expiryDate', type: 'date' },
    masterAgreementId: { name: 'masterAgreementId', type: 'string' },
    secondaryCommodityId: { name: 'secondaryCommodityId', type: 'string' },
    sourceLocation: { name: 'sourceLocation', type: 'string' },
    systemIdentifier: { name: 'systemIdentifier', type: 'string' },
    expansion1: { name: 'expansion1', type: 'string' },
    expansion2: { name: 'expansion2', type: 'string' },
    expansion3: { name: 'expansion3', type: 'string' },
    expansion4: { name: 'expansion4', type: 'string' },
    expansion5: { name: 'expansion5', type: 'string' },
    expansion6: { name: 'expansion6', type: 'string' },
    expansion7: { name: 'expansion7', type: 'string' },
    expansion8: { name: 'expansion8', type: 'string' },
    expansion9: { name: 'expansion9', type: 'string' },
    expansion10: { name: 'expansion10', type: 'string' },
    expansion11: { name: 'expansion11', type: 'string' },
    expansion12: { name: 'expansion12', type: 'string' },
    expansion13: { name: 'expansion13', type: 'string' },
    expansion14: { name: 'expansion14', type: 'string' },
    expansion15: { name: 'expansion15', type: 'string' },
    expansion16: { name: 'expansion16', type: 'string' },
    expansion17: { name: 'expansion17', type: 'string' },
    expansion18: { name: 'expansion18', type: 'string' },
    expansion19: { name: 'expansion19', type: 'string' },
    expansion20: { name: 'expansion20', type: 'string' },
};
